import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Plus, Trash2, Save, Upload, Play, FileText } from 'lucide-react'
import type { 
  GameFile, 
  ParameterDefinition, 
  PhaseDefinition, 
  ActionDefinition, 
  EventDefinition,
  Condition,
  Effect 
} from '@creanizer/types'

interface StructuredModePanelProps {
  onStartGame: (gameFile: GameFile) => void
  savedGameFiles: GameFile[]
  onSaveGameFile: (gameFile: GameFile) => void
  onLoadGameFile: (gameFile: GameFile) => void
  initialGameFile?: GameFile
}

export function StructuredModePanel({ 
  onStartGame, 
  savedGameFiles, 
  onSaveGameFile,
  onLoadGameFile,
  initialGameFile 
}: StructuredModePanelProps) {
  const [gameName, setGameName] = useState(initialGameFile?.name || '')
  const [gameDescription, setGameDescription] = useState(initialGameFile?.description || '')
  const [systemPrompt, setSystemPrompt] = useState(
    initialGameFile?.structuredMode?.systemPrompt || 'あなたは構造化されたゲームのゲームマスターです。定義されたルールに従ってゲームを進行してください。'
  )
  const [isSaved, setIsSaved] = useState(false)
  const [lastSavedFile, setLastSavedFile] = useState<GameFile | null>(null)
  
  // パラメータ定義
  const [parameters, setParameters] = useState<ParameterDefinition[]>(
    initialGameFile?.structuredMode?.parameters || [
      { name: 'health', displayName: 'HP', type: 'number', initialValue: 100, min: 0, max: 100, visible: true },
      { name: 'stamina', displayName: 'スタミナ', type: 'number', initialValue: 50, min: 0, max: 50, visible: true }
    ]
  )
  
  // フェーズ定義
  const [phases, setPhases] = useState<PhaseDefinition[]>(
    initialGameFile?.structuredMode?.phases || [
      { id: 'exploration', name: '探索', description: '世界を探索します', availableActions: ['move', 'search', 'rest'] }
    ]
  )
  
  // アクション定義
  const [actions, setActions] = useState<ActionDefinition[]>(
    initialGameFile?.structuredMode?.actions || [
      { id: 'move', name: '移動', description: '別の場所へ移動します' },
      { id: 'search', name: '調べる', description: '周囲を調査します' },
      { id: 'rest', name: '休憩', description: 'HPとスタミナを回復します' }
    ]
  )
  
  // 勝利条件
  const [winConditions, setWinConditions] = useState<Condition[]>(
    initialGameFile?.structuredMode?.winConditions || []
  )
  
  // 敗北条件
  const [loseConditions, setLoseConditions] = useState<Condition[]>(
    initialGameFile?.structuredMode?.loseConditions || [
      { type: 'parameter', target: 'health', operator: '<=', value: 0 }
    ]
  )

  // パラメータ追加
  const addParameter = () => {
    setParameters([...parameters, {
      name: '',
      displayName: '',
      type: 'number',
      initialValue: 0,
      visible: true
    }])
    if (isSaved) setIsSaved(false)
  }

  // パラメータ更新
  const updateParameter = (index: number, field: keyof ParameterDefinition, value: any) => {
    const updated = [...parameters]
    updated[index] = { ...updated[index], [field]: value }
    setParameters(updated)
    if (isSaved) setIsSaved(false)
  }

  // パラメータ削除
  const removeParameter = (index: number) => {
    setParameters(parameters.filter((_, i) => i !== index))
  }

  // アクション追加
  const addAction = () => {
    setActions([...actions, {
      id: '',
      name: '',
      description: ''
    }])
    if (isSaved) setIsSaved(false)
  }

  // アクション更新
  const updateAction = (index: number, field: keyof ActionDefinition, value: any) => {
    const updated = [...actions]
    updated[index] = { ...updated[index], [field]: value }
    setActions(updated)
    if (isSaved) setIsSaved(false)
  }

  // アクション削除
  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index))
  }

  // フェーズ追加
  const addPhase = () => {
    setPhases([...phases, {
      id: '',
      name: '',
      description: ''
    }])
  }

  // フェーズ更新
  const updatePhase = (index: number, field: keyof PhaseDefinition, value: any) => {
    const updated = [...phases]
    updated[index] = { ...updated[index], [field]: value }
    setPhases(updated)
    if (isSaved) setIsSaved(false)
  }

  // フェーズ削除
  const removePhase = (index: number) => {
    setPhases(phases.filter((_, i) => i !== index))
  }

  // 条件追加
  const addCondition = (type: 'win' | 'lose') => {
    const newCondition: Condition = {
      type: 'parameter',
      target: '',
      operator: '==',
      value: 0
    }
    
    if (type === 'win') {
      setWinConditions([...winConditions, newCondition])
    } else {
      setLoseConditions([...loseConditions, newCondition])
    }
  }

  // ゲームファイル作成
  const createGameFile = (): GameFile => {
    return {
      id: `game_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      name: gameName,
      description: gameDescription,
      version: '1.0.0',
      mode: 'structured',
      structuredMode: {
        systemPrompt,
        parameters: parameters.filter(p => p.name),
        phases: phases.filter(p => p.id),
        actions: actions.filter(a => a.id),
        winConditions,
        loseConditions,
        limits: {
          maxTurns: 100
        }
      },
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: 'ユーザー',
        language: 'ja',
        difficulty: 'normal'
      }
    }
  }

  // ゲーム開始
  const handleStartGame = () => {
    if (!gameName.trim()) {
      alert('ゲーム名を入力してください')
      return
    }
    
    const gameFile = createGameFile()
    onStartGame(gameFile)
  }

  // ゲームファイル保存
  const handleSaveGameFile = () => {
    if (!gameName.trim()) {
      alert('ゲーム名を入力してください')
      return
    }
    
    const gameFile = createGameFile()
    onSaveGameFile(gameFile)
    setIsSaved(true)
    setLastSavedFile(gameFile)
    
    // 2秒後に保存済み状態を解除（次の編集に備えて）
    setTimeout(() => {
      setIsSaved(false)
    }, 2000)
  }
  
  // 内容が変更されたら保存済み状態を解除
  const handleContentChange = (setter: (value: any) => void) => {
    return (value: any) => {
      setter(value)
      if (isSaved) {
        setIsSaved(false)
      }
    }
  }

  // ゲームファイルロード
  const handleLoadGameFile = (gameFile: GameFile) => {
    setGameName(gameFile.name)
    setGameDescription(gameFile.description)
    
    if (gameFile.structuredMode) {
      setSystemPrompt(gameFile.structuredMode.systemPrompt)
      setParameters(gameFile.structuredMode.parameters || [])
      setPhases(gameFile.structuredMode.phases || [])
      setActions(gameFile.structuredMode.actions || [])
      setWinConditions(gameFile.structuredMode.winConditions || [])
      setLoseConditions(gameFile.structuredMode.loseConditions || [])
    }
    
    onLoadGameFile(gameFile)
  }

  return (
    <div className="space-y-6">
      {/* 保存済みゲームファイル */}
      {savedGameFiles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>保存済みゲームファイル</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {savedGameFiles.filter(f => f.mode === 'structured').map((file) => (
                <div key={file.id} className="flex items-center justify-between p-2 border rounded">
                  <div>
                    <div className="font-medium">{file.name}</div>
                    <div className="text-sm text-gray-500">{file.description}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleLoadGameFile(file)}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      ロード
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        handleLoadGameFile(file)
                        onStartGame(file)
                      }}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      開始
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ゲームエディタ */}
      <Card>
        <CardHeader>
          <CardTitle>構造化モードエディタ</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="basic" className="space-y-4">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">基本情報</TabsTrigger>
              <TabsTrigger value="parameters">パラメータ</TabsTrigger>
              <TabsTrigger value="phases">フェーズ</TabsTrigger>
              <TabsTrigger value="actions">アクション</TabsTrigger>
              <TabsTrigger value="conditions">条件</TabsTrigger>
            </TabsList>

            {/* 基本情報 */}
            <TabsContent value="basic" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="game-name">ゲーム名 *</Label>
                <Input
                  id="game-name"
                  value={gameName}
                  onChange={(e) => handleContentChange(setGameName)(e.target.value)}
                  placeholder="例: ダンジョン探索"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="game-desc">説明</Label>
                <Textarea
                  id="game-desc"
                  value={gameDescription}
                  onChange={(e) => handleContentChange(setGameDescription)(e.target.value)}
                  placeholder="ゲームの概要..."
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="system-prompt">システムプロンプト</Label>
                <Textarea
                  id="system-prompt"
                  value={systemPrompt}
                  onChange={(e) => handleContentChange(setSystemPrompt)(e.target.value)}
                  rows={4}
                  className="font-mono text-sm"
                />
              </div>
            </TabsContent>

            {/* パラメータ */}
            <TabsContent value="parameters" className="space-y-4">
              <div className="space-y-2">
                {parameters.map((param, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label>内部名</Label>
                      <Input
                        value={param.name}
                        onChange={(e) => updateParameter(index, 'name', e.target.value)}
                        placeholder="health"
                      />
                    </div>
                    <div className="flex-1">
                      <Label>表示名</Label>
                      <Input
                        value={param.displayName}
                        onChange={(e) => updateParameter(index, 'displayName', e.target.value)}
                        placeholder="HP"
                      />
                    </div>
                    <div className="w-24">
                      <Label>初期値</Label>
                      <Input
                        type="number"
                        value={param.initialValue}
                        onChange={(e) => updateParameter(index, 'initialValue', Number(e.target.value))}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeParameter(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addParameter}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  パラメータ追加
                </Button>
              </div>
            </TabsContent>

            {/* フェーズ */}
            <TabsContent value="phases" className="space-y-4">
              <div className="space-y-2">
                {phases.map((phase, index) => (
                  <div key={index} className="border rounded p-3 space-y-2">
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label>ID</Label>
                        <Input
                          value={phase.id}
                          onChange={(e) => updatePhase(index, 'id', e.target.value)}
                          placeholder="exploration"
                        />
                      </div>
                      <div className="flex-1">
                        <Label>名前</Label>
                        <Input
                          value={phase.name}
                          onChange={(e) => updatePhase(index, 'name', e.target.value)}
                          placeholder="探索"
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removePhase(index)}
                        className="mt-6"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div>
                      <Label>説明</Label>
                      <Textarea
                        value={phase.description}
                        onChange={(e) => updatePhase(index, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addPhase}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  フェーズ追加
                </Button>
              </div>
            </TabsContent>

            {/* アクション */}
            <TabsContent value="actions" className="space-y-4">
              <div className="space-y-2">
                {actions.map((action, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label>ID</Label>
                      <Input
                        value={action.id}
                        onChange={(e) => updateAction(index, 'id', e.target.value)}
                        placeholder="move"
                      />
                    </div>
                    <div className="flex-1">
                      <Label>名前</Label>
                      <Input
                        value={action.name}
                        onChange={(e) => updateAction(index, 'name', e.target.value)}
                        placeholder="移動"
                      />
                    </div>
                    <div className="flex-[2]">
                      <Label>説明</Label>
                      <Input
                        value={action.description}
                        onChange={(e) => updateAction(index, 'description', e.target.value)}
                        placeholder="別の場所へ移動します"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeAction(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addAction}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  アクション追加
                </Button>
              </div>
            </TabsContent>

            {/* 条件 */}
            <TabsContent value="conditions" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label>勝利条件</Label>
                  <div className="space-y-2 mt-2">
                    {winConditions.map((condition, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Input
                          value={condition.target}
                          placeholder="パラメータ名"
                          className="w-32"
                        />
                        <select 
                          value={condition.operator}
                          className="px-3 py-2 border rounded"
                        >
                          <option value="==">==</option>
                          <option value="!=">!=</option>
                          <option value=">">{">"}</option>
                          <option value="<">{"<"}</option>
                          <option value=">=">{">="}</option>
                          <option value="<=">{"<="}</option>
                        </select>
                        <Input
                          value={condition.value}
                          placeholder="値"
                          className="w-24"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setWinConditions(winConditions.filter((_, i) => i !== index))}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addCondition('win')}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      勝利条件追加
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>敗北条件</Label>
                  <div className="space-y-2 mt-2">
                    {loseConditions.map((condition, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Input
                          value={condition.target}
                          onChange={(e) => {
                            const updated = [...loseConditions]
                            updated[index] = { ...updated[index], target: e.target.value }
                            setLoseConditions(updated)
                          }}
                          placeholder="パラメータ名"
                          className="w-32"
                        />
                        <select 
                          value={condition.operator}
                          onChange={(e) => {
                            const updated = [...loseConditions]
                            updated[index] = { ...updated[index], operator: e.target.value as any }
                            setLoseConditions(updated)
                          }}
                          className="px-3 py-2 border rounded"
                        >
                          <option value="==">==</option>
                          <option value="!=">!=</option>
                          <option value=">">{">"}</option>
                          <option value="<">{"<"}</option>
                          <option value=">=">{">="}</option>
                          <option value="<=">{"<="}</option>
                        </select>
                        <Input
                          type="number"
                          value={condition.value}
                          onChange={(e) => {
                            const updated = [...loseConditions]
                            updated[index] = { ...updated[index], value: Number(e.target.value) }
                            setLoseConditions(updated)
                          }}
                          placeholder="値"
                          className="w-24"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setLoseConditions(loseConditions.filter((_, i) => i !== index))}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addCondition('lose')}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      敗北条件追加
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* アクションボタン */}
          <div className="flex gap-2 mt-6">
            <Button
              onClick={handleSaveGameFile}
              variant={isSaved ? 'secondary' : 'outline'}
              disabled={isSaved}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaved ? '保存済み' : '保存'}
            </Button>
            <Button
              onClick={handleStartGame}
              disabled={!gameName.trim()}
              className="flex-1"
            >
              <Play className="w-4 h-4 mr-2" />
              このゲームを開始
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}